import { getIndexByKey } from "@utils/search-helpers";
import { convertToStringLower, numericOnly } from "@utils/string-helpers";

export const ascSort = (key) => (a, b) => {
  let ItemA = convertToStringLower(a[key]),
    ItemB = convertToStringLower(b[key]);
  if (numericOnly(ItemA) && numericOnly(ItemB)) return ItemA - ItemB;
  else {
    return ItemA > ItemB ? 1 : ItemA < ItemB ? -1 : 0;
  }
};

// sort array alpha-numeric string combinations, key param for array of objects
export function naturalSort(
  arr,
  key,
  opts = { numeric: true, sensitivity: "base" }
) {
  return key
    ? arr.sort((a, b) =>
        `${a[key]}`.localeCompare(`${b[key]}`, undefined, opts)
      )
    : arr.sort((a, b) => `${a}`.localeCompare(`${b}`, undefined, opts));
}

// used by promotions and banners reducers
export function convertArrToObj(arr = [], valueToKey) {
  if (valueToKey && arr[0].hasOwnProperty(valueToKey)) {
    let newObj = {};
    arr.forEach(function (el) {
      newObj[el[valueToKey]] = el;
    });
    return newObj;
  } else {
    throw new Error(`${valueToKey} key is not found.`);
  }
}

export function reduceArrayOfObjToObj(arr, key) {
  if (!arr || !Array.isArray(arr)) return {};
  try {
    return arr.reduce((obj, item) => ((obj[item[key]] = item), obj), {});
  } catch (err) {
    console.warn(err);
    return {};
  }
}

// from array of objects, creates object with keys based on given key and assigns defaults
export const arrObjsToObjectDefaults = (
  arr = [],
  key,
  defaultVal,
  withLambda = null
) => {
  return arr.reduce((obj, item) => {
    obj[item[key]] = withLambda ? withLambda(item) : defaultVal;
    return obj;
  }, {});
};

// Object.keys returns string values, fn converts key strings to numbers
export function objKeysToNumericArray(obj) {
  return Object.keys(obj).map(Number);
}

export function removeDupArrItems(arr, arr2) {
  return arr.filter((item) => !arr2.includes(item));
}

export const onlyUniqueItems = (arr, key = "", applyKey = false) => {
  let seenItems = {};
  if (arr) {
    arr.forEach((item) => {
      item
        ? applyKey
          ? (seenItems[item[key]] = item)
          : (seenItems[item] = item)
        : null;
    });
    return Object.values(seenItems);
  }
  return [];
};

// TODO: remove along with deprecating old App Config work
export function toggleArrItem(array, item) {
  return array.includes(item)
    ? array.filter((el) => {
        return el !== item;
      })
    : [...array, item];
}

export function sortByKeyAndOrder(arr, key, order = "ASC") {
  let sorttedArry = arr ? arr.sort(ascSort(key)) : [];
  return order === "ASC"
    ? sorttedArry
    : sorttedArry
    ? sorttedArry.reduce((acc, item) => [item, ...acc], [])
    : [];
}

export function searchAndReplaceByObjKey(key, value, arr, newVal) {
  let returnInd = getIndexByKey(key, value, arr);
  if (returnInd !== -1) {
    arr[returnInd] = newVal;
  }
  return arr;
}
export function arrOfObjReplaceObjKey(key, value, arr, newVal) {
  arr = arr.map((el) => (el[key] === value ? { ...el, key: newVal } : el));
  return arr;
}

export function searchAndRemoveByKeyVal(key, value, arr) {
  let returnInd = getIndexByKey(key, value, arr);
  if (returnInd !== -1) {
    arr.splice(returnInd, 1);
  }
  return arr;
}

export function newArrUpdate(arr, index, value, deleteLastItem = true) {
  return value
    ? [
        ...arr.slice(0, index),
        value,
        ...(deleteLastItem ? arr.slice(index + 1) : arr.slice(index)),
      ]
    : [...arr.slice(0, index), ...arr.slice(index + 1)];
}

export const createRange = (initialVal, lastVal, step = 1) => {
  if (
    typeof initialVal !== "number" ||
    typeof lastVal !== "number" ||
    typeof step !== "number"
  ) {
    throw new TypeError("Range given must be number.");
  }
  let i = initialVal;
  const range = [];
  while (i <= lastVal) {
    range.push(i);
    i += step;
  }
  return range;
};

export const getArrayOfObjsWithKey = (arr, key, byHaving = false) => {
  return arr.filter((item) =>
    byHaving ? item[key] !== undefined : item[key] === undefined
  );
};

export const setArrayOfObjsWithKey = (
  arr,
  key,
  defaultValue,
  overwrite = false
) => {
  return arr.map((item) =>
    item[key] === undefined
      ? { ...item, [key]: defaultValue }
      : { ...item, ...(overwrite ? { [key]: defaultValue } : {}) }
  );
};

export const sortArrayOfStringByIndexOfSubString = (
  a,
  b,
  input,
  ignoreCase = true
) => {
  input = ignoreCase ? convertToStringLower(input) : `${input}`;
  let aIndex = (ignoreCase ? convertToStringLower(a) : `${a}`).indexOf(input);
  let bIndex = (ignoreCase ? convertToStringLower(b) : `${b}`).indexOf(input);
  return aIndex > bIndex ? 1 : bIndex > aIndex ? -1 : 0;
};

export const createArrayFromInnerArrayOfObjects = (arr, key) => {
  let updatedArr = [];
  if (arr) {
    arr.forEach(
      (item) =>
        (updatedArr = [...updatedArr, ...(item && item[key] ? item[key] : [])])
    );
  }
  return updatedArr;
};

export const arrayIntersection = (smallArr, largeArr) => {
  return smallArr.filter((item) => largeArr.includes(item));
};

export const arrayDiff = (arr1, arr2) => {
  return arr1
    .filter((item) => !arr2.includes(item))
    .concat(arr2.filter((item) => !arr1.includes(item)));
};

export const arrayUnion = (arr1, arr2) => {
  return arr1.filter((item) => !arr2.includes(item)).concat(arr2);
};
export const findMaxMinByKey = (arr, key, findMax = true) => {
  if (!Array.isArray(arr)) {
    throw new TypeError("Invalid type, value must be array");
  }
  return arr && arr.length
    ? arr.reduce(function (current, next) {
        return findMax
          ? current[key] > next[key]
            ? current
            : next
          : current[key] < next[key]
          ? current
          : next;
      })
    : {};
};

/* does not allow duplicate values in array */
export const addToOrRemoveFromArray = (action, value, arr, reverse = false) => {
  var index = arr.indexOf(value);
  if (action === "remove" && index !== -1) {
    arr.splice(index, 1);
    return arr;
  } else if (action === "add" && index === -1) {
    return reverse ? [...arr, value] : [value, ...arr];
  } else {
    return arr;
  }
};

// returns true if arr1 contains all values in arr2
export const containsAllValues = (arr1, arr2) => {
  return arr2.every((value) => arr1.includes(value));
};

/* checks if two 2-d arrays have the same contents, send orderMatters as true if order matters */
export const arraysAreEqual = (arr1, arr2, orderMatters = false) => {
  if (!orderMatters) {
    arr1 = [...arr1].sort();
    arr2 = [...arr2].sort();
  }
  return arr1.every((value, index) => value === arr2[index]);
};
